import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../App';
import { apiRequest } from '../api';

export default function UsersPage() {
  const { authToken, currentUser, tenantUsers, cloudAccounts } = useApp();

  const currentRole = (currentUser?.role || '').toLowerCase();
  const canManageAccess = ['owner', 'admin', 'superadmin'].includes((currentUser?.role || '').toLowerCase());
  const canManageWorkspaceRoles = ['owner', 'superadmin'].includes(currentRole);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [roleBusyByUserId, setRoleBusyByUserId] = useState({});
  const [invitationsAccountId, setInvitationsAccountId] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [accountFilter, setAccountFilter] = useState('');
  const [invitations, setInvitations] = useState([]);
  const [invitesBusy, setInvitesBusy] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [revokeBusyById, setRevokeBusyById] = useState({});
  const [resendBusyById, setResendBusyById] = useState({});
  const [pageError, setPageError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [acceptToken, setAcceptToken] = useState('');
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [form, setForm] = useState({ invited_email: '', invited_role: 'viewer', expires_in_days: 7 });

  const accountOptions = useMemo(() => cloudAccounts || [], [cloudAccounts]);
  const filteredAccounts = useMemo(() => {
    const query = accountFilter.trim().toLowerCase();
    if (!query) return accountOptions;
    return accountOptions.filter((account) => {
      const provider = (account.provider || '').toLowerCase();
      const accountId = (account.account_id || '').toLowerCase();
      return provider.includes(query) || accountId.includes(query);
    });
  }, [accountFilter, accountOptions]);
  const selectedAccountIdSet = useMemo(() => new Set(selectedAccountIds), [selectedAccountIds]);

  useEffect(() => {
    setWorkspaceUsers(tenantUsers || []);
  }, [tenantUsers]);

  const loadInvitations = useCallback(
    async (cloudAccountId) => {
      if (!canManageAccess || !authToken || !cloudAccountId) {
        setInvitations([]);
        return;
      }
      setInvitesBusy(true);
      setPageError('');
      try {
        const data = await apiRequest(`/api/v1/cloud-accounts/${cloudAccountId}/invitations`, {}, authToken);
        setInvitations(data || []);
      } catch (err) {
        setInvitations([]);
        setPageError(err.message || 'Could not load invitations.');
      } finally {
        setInvitesBusy(false);
      }
    },
    [canManageAccess, authToken],
  );

  useEffect(() => {
    if (accountOptions.length === 0) {
      setInvitationsAccountId('');
      setSelectedAccountIds([]);
      return;
    }

    setInvitationsAccountId((prev) => {
      if (prev && accountOptions.some((account) => account.id === prev)) {
        return prev;
      }
      return accountOptions[0].id;
    });

    setSelectedAccountIds((prev) => prev.filter((id) => accountOptions.some((account) => account.id === id)));
  }, [accountOptions]);

  useEffect(() => {
    loadInvitations(invitationsAccountId);
  }, [loadInvitations, invitationsAccountId]);

  function toggleAccountSelection(accountId) {
    setSelectedAccountIds((prev) => {
      if (prev.includes(accountId)) {
        return prev.filter((id) => id !== accountId);
      }
      return [...prev, accountId];
    });
  }

  function selectVisibleAccounts() {
    if (filteredAccounts.length === 0) return;
    setSelectedAccountIds((prev) => {
      const next = new Set(prev);
      filteredAccounts.forEach((account) => next.add(account.id));
      return Array.from(next);
    });
  }

  function clearSelectedAccounts() {
    setSelectedAccountIds([]);
  }

  async function createInvitation(e) {
    e.preventDefault();
    if (!canManageAccess || selectedAccountIds.length === 0) return;

    setCreateBusy(true);
    setPageError('');
    setPageMessage('');
    try {
      const body = {
        invited_email: form.invited_email,
        invited_role: form.invited_role,
        expires_in_days: Number(form.expires_in_days || 7),
      };
      const results = await Promise.allSettled(
        selectedAccountIds.map((accountId) =>
          apiRequest(
            `/api/v1/cloud-accounts/${accountId}/invitations`,
            {
              method: 'POST',
              body: JSON.stringify(body),
            },
            authToken,
          ),
        ),
      );

      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failureMessages = results
        .filter((result) => result.status === 'rejected')
        .map((result) => result.reason?.message || 'Invitation failed');

      if (successCount === 0) {
        throw new Error(failureMessages[0] || 'Could not create invitations.');
      }

      setForm({ invited_email: '', invited_role: 'viewer', expires_in_days: 7 });

      if (!selectedAccountIds.includes(invitationsAccountId)) {
        setInvitationsAccountId(selectedAccountIds[0]);
      } else {
        await loadInvitations(invitationsAccountId);
      }

      const summary = `${successCount} invitation${successCount === 1 ? '' : 's'} created. ${successCount} invitation email${successCount === 1 ? '' : 's'} sent.`;
      if (failureMessages.length > 0) {
        setPageMessage(`${summary} ${failureMessages.length} failed. First error: ${failureMessages[0]}`);
      } else {
        setPageMessage(summary);
      }
    } catch (err) {
      setPageError(err.message || 'Could not create invitation.');
    } finally {
      setCreateBusy(false);
    }
  }

  async function revokeInvitation(invitationId) {
    if (!canManageAccess) return;
    setRevokeBusyById((prev) => ({ ...prev, [invitationId]: true }));
    setPageError('');
    setPageMessage('');
    try {
      await apiRequest(`/api/v1/cloud-accounts/invitations/${invitationId}/revoke`, { method: 'POST' }, authToken);
      setPageMessage('Invitation revoked.');
      await loadInvitations(invitationsAccountId);
    } catch (err) {
      setPageError(err.message || 'Could not revoke invitation.');
    } finally {
      setRevokeBusyById((prev) => {
        const next = { ...prev };
        delete next[invitationId];
        return next;
      });
    }
  }

  async function resendInvitation(invitationId, mode = 'resend') {
    if (!canManageAccess) return;
    setResendBusyById((prev) => ({ ...prev, [invitationId]: true }));
    setPageError('');
    setPageMessage('');
    try {
      const response = await apiRequest(
        `/api/v1/cloud-accounts/invitations/${invitationId}/resend`,
        { method: 'POST' },
        authToken,
      );
      const baseMessage = mode === 'reinvite'
        ? (response.message || 'Invitation re-issued.')
        : (response.message || 'Invitation resent.');
      if (response.invitation_token) {
        setPageMessage(`${baseMessage} Token: ${response.invitation_token}`);
      } else {
        setPageMessage(baseMessage);
      }
      await loadInvitations(invitationsAccountId);
    } catch (err) {
      setPageError(err.message || 'Could not resend invitation.');
    } finally {
      setResendBusyById((prev) => {
        const next = { ...prev };
        delete next[invitationId];
        return next;
      });
    }
  }

  async function acceptInvitation(e) {
    e.preventDefault();
    if (!acceptToken.trim()) return;

    setAcceptBusy(true);
    setPageError('');
    setPageMessage('');
    try {
      const response = await apiRequest(
        '/api/v1/cloud-accounts/invitations/accept',
        {
          method: 'POST',
          body: JSON.stringify({ token: acceptToken.trim() }),
        },
        authToken,
      );
      setAcceptToken('');
      setPageMessage(`Access granted for account ${response.cloud_account_id} with role ${response.role}.`);
    } catch (err) {
      setPageError(err.message || 'Could not accept invitation.');
    } finally {
      setAcceptBusy(false);
    }
  }

  async function updateWorkspaceUserRole(userId, nextRole) {
    if (!canManageWorkspaceRoles) return;
    setRoleBusyByUserId((prev) => ({ ...prev, [userId]: true }));
    setPageError('');
    setPageMessage('');
    try {
      const updated = await apiRequest(
        `/api/v1/auth/users/${userId}/role`,
        {
          method: 'PATCH',
          body: JSON.stringify({ role: nextRole }),
        },
        authToken,
      );
      setWorkspaceUsers((prev) => prev.map((user) => (user.id === userId ? { ...user, role: updated.role } : user)));
      setPageMessage(`Updated ${updated.email} role to ${updated.role}.`);
    } catch (err) {
      setPageError(err.message || 'Could not update user role.');
    } finally {
      setRoleBusyByUserId((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }
  }

  return (
    <div className="page-view">
      <div className="page-header">
        <p className="feature-eyebrow">Users</p>
        <h2>User access management</h2>
        <p className="page-subtitle">Manage workspace users and account access invitations from one place.</p>
      </div>

      {pageError && <p className="auth-feedback auth-error">{pageError}</p>}
      {pageMessage && !pageError && <p className="auth-feedback auth-success">{pageMessage}</p>}

      <div className="workspace-block">
        <h4>Workspace users</h4>
        <ul className="card-list compact-list">
          {(workspaceUsers || []).map((user) => (
            <li key={user.id}>
              <div className="account-row">
                <div>
                  <strong>{user.full_name || user.email}</strong>
                  <span className="account-hint">{user.email}</span>
                </div>
                {canManageWorkspaceRoles &&
                user.id !== currentUser?.id &&
                user.role !== 'owner' &&
                !(user.role === 'superadmin' && currentRole !== 'superadmin') ? (
                  <select
                    className="input-sm"
                    value={user.role}
                    disabled={!!roleBusyByUserId[user.id]}
                    onChange={(e) => updateWorkspaceUserRole(user.id, e.target.value)}
                  >
                    <option value="viewer">viewer</option>
                    <option value="analyst">analyst</option>
                    <option value="admin">admin</option>
                    <option value="security_auditor">security_auditor</option>
                    <option value="webmaster_admin">webmaster_admin</option>
                    {currentRole === 'superadmin' && <option value="superadmin">superadmin</option>}
                  </select>
                ) : (
                  <span className="badge badge-ready">{user.role}</span>
                )}
              </div>
            </li>
          ))}
          {(workspaceUsers || []).length === 0 && (
            <li>
              <p className="form-hint">No users found in this workspace.</p>
            </li>
          )}
        </ul>
      </div>

      {canManageAccess && (
        <div className="workspace-block">
          <h4>Invite a user to cloud accounts</h4>
          {accountOptions.length === 0 ? (
            <p className="form-hint">Add a cloud account first, then create invitations.</p>
          ) : (
            <form className="auth-form inline-form" onSubmit={createInvitation}>
              <label>
                Search accounts
                <input
                  type="text"
                  placeholder="Filter by provider or account id"
                  value={accountFilter}
                  onChange={(e) => setAccountFilter(e.target.value)}
                />
              </label>
              <div className="form-actions">
                <button className="secondary-action inline-action" type="button" onClick={selectVisibleAccounts}>
                  Select visible
                </button>
                <button className="secondary-action inline-action" type="button" onClick={clearSelectedAccounts}>
                  Clear selection
                </button>
                <span className="form-hint">Selected: {selectedAccountIds.length}</span>
              </div>
              <div className="invitation-account-grid">
                {filteredAccounts.map((account) => (
                  <label className="invitation-account-item" key={account.id}>
                    <input
                      type="checkbox"
                      checked={selectedAccountIdSet.has(account.id)}
                      onChange={() => toggleAccountSelection(account.id)}
                    />
                    <span>
                      <strong>{account.provider.toUpperCase()}</strong> | {account.account_id}
                    </span>
                  </label>
                ))}
              </div>
              {filteredAccounts.length === 0 && <p className="form-hint">No cloud accounts match this filter.</p>}
              <label>
                User email
                <input
                  type="email"
                  placeholder="user@company.com"
                  value={form.invited_email}
                  onChange={(e) => setForm((prev) => ({ ...prev, invited_email: e.target.value }))}
                  required
                />
              </label>
              <div className="form-row">
                <label>
                  Role
                  <select
                    value={form.invited_role}
                    onChange={(e) => setForm((prev) => ({ ...prev, invited_role: e.target.value }))}
                  >
                    <option value="viewer">viewer</option>
                    <option value="analyst">analyst</option>
                    <option value="admin">admin</option>
                  </select>
                </label>
                <label>
                  Expires (days)
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={form.expires_in_days}
                    onChange={(e) => setForm((prev) => ({ ...prev, expires_in_days: e.target.value }))}
                  />
                </label>
              </div>
              <div className="form-actions">
                <button className="primary-action" type="submit" disabled={createBusy || selectedAccountIds.length === 0}>
                  {createBusy ? 'Creating…' : 'Create invitation'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="workspace-block">
        <h4>Accept account access invitation</h4>
        <p className="page-subtitle">Paste invitation token to activate access to a cloud account.</p>
        <form className="auth-form inline-form" onSubmit={acceptInvitation}>
          <label>
            Invitation token
            <input
              type="text"
              placeholder="sroik_inv_..."
              value={acceptToken}
              onChange={(e) => setAcceptToken(e.target.value)}
            />
          </label>
          <div className="form-actions">
            <button className="primary-action" type="submit" disabled={acceptBusy || !acceptToken.trim()}>
              {acceptBusy ? 'Accepting…' : 'Accept invitation'}
            </button>
          </div>
        </form>
      </div>

      {canManageAccess && invitationsAccountId && (
        <div className="workspace-block">
          <h4>Invitations by account</h4>
          <p className="form-hint">Invitation status is specific to the selected cloud account.</p>
          <div className="auth-form">
            <label>
              Cloud account
              <select value={invitationsAccountId} onChange={(e) => setInvitationsAccountId(e.target.value)}>
                {accountOptions.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.provider.toUpperCase()} | {account.account_id}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {invitesBusy ? (
            <p className="form-hint">Loading invitations…</p>
          ) : (
            <ul className="card-list compact-list">
              {invitations.map((invitation) => (
                <li key={invitation.id}>
                  <div className="account-row">
                    <div>
                      <strong>{invitation.invited_email}</strong>
                      <span className="account-hint">
                        role {invitation.invited_role} | status {invitation.status_label || invitation.status}
                      </span>
                    </div>
                    {invitation.status === 'pending' && (
                      <div className="account-actions">
                        <button
                          className="secondary-action inline-action"
                          type="button"
                          disabled={!!resendBusyById[invitation.id]}
                          onClick={() => resendInvitation(invitation.id)}
                        >
                          {resendBusyById[invitation.id] ? 'Resending…' : 'Resend invite'}
                        </button>
                        <button
                          className="secondary-action inline-action danger-action"
                          type="button"
                          disabled={!!revokeBusyById[invitation.id]}
                          onClick={() => revokeInvitation(invitation.id)}
                        >
                          {revokeBusyById[invitation.id] ? 'Revoking…' : 'Revoke'}
                        </button>
                      </div>
                    )}
                    {['revoked', 'expired'].includes(invitation.status) && (
                      <div className="account-actions">
                        <button
                          className="secondary-action inline-action"
                          type="button"
                          disabled={!!resendBusyById[invitation.id]}
                          onClick={() => resendInvitation(invitation.id, 'reinvite')}
                        >
                          {resendBusyById[invitation.id] ? 'Re-inviting…' : 'Re-invite'}
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
              {invitations.length === 0 && (
                <li>
                  <p className="form-hint">No invitations yet for this account.</p>
                </li>
              )}
            </ul>
          )}
        </div>
      )}

    </div>
  );
}
